import { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { useForm, FormProvider } from 'react-hook-form'
import { DEFAULT_THEME_COLORS, DEFAULT_BORDER_STYLES } from 'css/theme'
import AmountCurrencyFrequencySection from '../../../flow/components/dcf-nudge-step/components/amount-currency-frequency-section'
import CheckboxField from 'modules/ui/molecules/FormComponents/CheckboxField'
import { Tooltip } from 'modules/ui/atoms/Tooltip'
import { UiIcon } from '../../../ui/atoms/Icon'
import IconContainer from '../../../ui/atoms/Layout/IconContainer'
import styled from '@emotion/styled'
import { getCurrencyDecimalPlaces } from '../../../../utils/currency'

const StyledDiv = styled.div`
  display: flex;
  justify-content: center;
`

const OrderDonorCoveredFees = () => {
  const { t, i18n } = useTranslation()
  const order = useOrder()
  const config = useConfig()

  type DonorCoveredFeesFormData = {
    dcf: boolean
  }

  const methods = useForm<DonorCoveredFeesFormData>({
    defaultValues: {
      dcf: order.dcf.value.isChecked,
    },
  })

  const uncheckedAmountValue = order.amount.value

  const checkedAmountValue = (order.amount.value ?? 0) + order.dcf.value.fee

  const [amountValue, setAmountValue] = useState(uncheckedAmountValue)

  // Listen to Checkbox changes and update content.
  const dcfChecked = methods.watch('dcf')

  useEffect(() => {
    order.dcf.setValue({ isChecked: dcfChecked })

    setAmountValue(
      dcfChecked
        ? Number(checkedAmountValue.toFixed(getCurrencyDecimalPlaces(order.currency.value)))
        : uncheckedAmountValue,
    )
  }, [dcfChecked, checkedAmountValue])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: config.dcf.isEnabled ? '8rem' : '6rem',
        padding: '16px',
        borderRadius: DEFAULT_BORDER_STYLES['--theme--border-radius-md'],
        border: config.dcf.isEnabled && !dcfChecked ? '1px solid' : '2px solid',
        backgroundColor:
          config.dcf.isEnabled && !dcfChecked
            ? DEFAULT_THEME_COLORS['--theme-color--neutral-95']
            : '',
        borderColor:
          config.dcf.isEnabled && !dcfChecked
            ? DEFAULT_THEME_COLORS['--theme-color--neutral-95']
            : config.theme['--theme-color__brand-primary'] ||
              DEFAULT_THEME_COLORS['--theme-color--brand-primary'],
        boxShadow:
          config.dcf.isEnabled && !dcfChecked ? 'none' : '0px 4px 8px rgba(25, 29, 34, 0.1)',
      }}
    >
      <span style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
        <AmountCurrencyFrequencySection
          amount={amountValue}
          currency={order.currency.value}
          frequency={order.frequency.value}
          locale={i18n.language}
        />
      </span>

      <span style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {config.dcf.isEnabled && (
          <FormProvider {...methods}>
            <form onSubmit={undefined}>
              <StyledDiv>
                <CheckboxField id="dcf" centered={true} dataId="options-step-toggle-dcf">
                  {t('orderDonorCoveredFees.coverFees.label', 'Cover transaction fees')}
                </CheckboxField>
                <IconContainer top="4px">
                  <Tooltip
                    placement="bottom-start"
                    text={t(
                      'orderDonorCoveredFees.tooltip.text',
                      'Transaction fees include both processing and donation software charges.',
                    )}
                    textAlign="left"
                  >
                    <UiIcon name="info" color="#78726E" />
                  </Tooltip>
                </IconContainer>
              </StyledDiv>
            </form>
          </FormProvider>
        )}
      </span>
    </div>
  )
}

export default OrderDonorCoveredFees
